import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { PrimaryButton, ArrowLink } from "./Buttons";
import { HeroArtwork } from "./HeroArtwork";
import { Eyebrow } from "./Reveal";

const MaskedLine = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <span className={`block overflow-hidden pb-1 ${className}`}>
      <motion.span
        className="block"
        initial={reduce ? false : { y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const Hero = ({ onBuild }) => {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  // Subtle parallax on decorative glows only; the product artwork stays static.
  const glowDrift = useTransform(scrollY, [0, 700], [0, reduce ? 0 : 90]);

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden pt-[120px] sm:pt-[150px]">
      <motion.div aria-hidden="true" style={{ y: glowDrift }} className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 top-10 h-80 w-80 rounded-full bg-lilac/[0.07] blur-3xl" />
        <div className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-lilac-deep/[0.08] blur-3xl" />
      </motion.div>

      <div className="relative mx-auto grid w-full max-w-shell items-center gap-14 px-5 pb-20 sm:px-8 lg:grid-cols-2 lg:gap-8 lg:pb-28">
        <div>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <Eyebrow testId="hero-eyebrow">Your idea. A complete business.</Eyebrow>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="mt-5 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-paper sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.08}>Your peptide brand.</MaskedLine>
            <MaskedLine delay={0.2} className="text-lilac">
              Ready to launch.
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-body"
            className="mt-6 max-w-md text-base text-paper/65 sm:text-lg"
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.34, ease: [0.22, 1, 0.36, 1] }}
          >
            From wholesale supply to your customer's doorstep — white-label vials, your brand and website,
            and fulfillment. Everything it takes to start your peptide business, all in one place.
          </motion.p>

          <motion.div
            id="hero-cta"
            className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-5"
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.46, ease: [0.22, 1, 0.36, 1] }}
          >
            <PrimaryButton testId="hero-build-button" onClick={onBuild}>
              Request a launch proposal
            </PrimaryButton>
            <ArrowLink testId="hero-whats-included-link" target="included" tone="paper" className="!text-paper/80 hover:!text-paper">
              See what's included
            </ArrowLink>
          </motion.div>

          <motion.p
            data-testid="hero-supporting-line"
            className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-paper/40"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.6 }}
          >
            <span>Wholesale supply</span>
            <span aria-hidden="true" className="text-lilac/50">/</span>
            <span>White-label vials</span>
            <span aria-hidden="true" className="text-lilac/50">/</span>
            <span>Brand &amp; website</span>
            <span aria-hidden="true" className="text-lilac/50">/</span>
            <span>3PL fulfillment</span>
          </motion.p>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.25 }}
        >
          <HeroArtwork />
        </motion.div>
      </div>
    </section>
  );
};
